import React, { useState } from "react"
import { X, Loader2 } from "lucide-react"
import { PlusIcon, Trash2Icon } from "@shared/icons"
import { useTranslation } from "react-i18next"
import { useDevice } from "@shared/hooks"
import { useLocalConfirmation } from "@shared/hooks/useLocalConfirmation"
import { formatDate } from "../utils/messageHelpers"
import type { UIText } from "../types"
import type { ChatMessage } from "@/graphql/main/gql/graphql"

interface Conversation {
  id?: string | null
  messages?: readonly ChatMessage[]
  createdAt?: string | null
}

interface ConversationsListProps {
  conversations: Conversation[]
  selectedConversation: string | null
  onSelectConversation: (id: string | null) => void
  onDeleteConversation: (id: string) => Promise<void>
  onNewChat: () => void
  deletingIds: Set<string>
  showConversationsList: boolean
  setShowConversationsList: (show: boolean) => void
  getChatTitle: (messages: readonly ChatMessage[], defaultTitle: string) => string
  uiText: UIText
}

const ConversationsListComponent: React.FC<ConversationsListProps> = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  onDeleteConversation,
  onNewChat,
  deletingIds,
  showConversationsList,
  setShowConversationsList,
  getChatTitle,
  uiText,
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [isHovering, setIsHovering] = useState<string | null>(null)
  const { showConfirmation, ConfirmationModal } = useLocalConfirmation()

  const handleConversationClick = (conv: Conversation) => {
    onSelectConversation(conv.id || null)
    if (isMobile) {
      setShowConversationsList(false)
    }
  }

  const handleDeleteClick = (e: React.MouseEvent, convId: string) => {
    e.stopPropagation()
    if (isMobile) {
      showConfirmation(t("chat.deleteThisChat"), () => onDeleteConversation(convId), { confirmColor: "red" })
      return
    }
    void onDeleteConversation(convId) // handleDeleteConversation catches and logs its own failures
  }

  return (
    <>
      {/* A 268px column of rows on a hairline — no tinted panel, no gradient stripe on the active
        row: selection is the blue-50 fill and a heavier title (docs/design/AI Assistant.dc.html). */}
      <div
        className={`
      ${isMobile ? (showConversationsList ? "absolute inset-0 z-50 bg-white" : "hidden") : ""}
      ${!isMobile ? "w-67 flex-none border-r border-border" : ""}
      flex min-h-0 flex-col
    `}
      >
        {isMobile && showConversationsList && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-3 py-3">
            <h3 className="text-[15px] font-semibold">{t("chat.chats")}</h3>
            <button
              onClick={() => setShowConversationsList(false)}
              aria-label={t("common.close")}
              className="flex size-10 flex-none items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-secondary hover:text-gray-900"
            >
              <X size={15} />
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return
                if (e.key !== "Enter" && e.key !== " ") return
                e.preventDefault()
                handleConversationClick(conv)
              }}
              className={`flex min-h-12 cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 transition-colors max-md:min-h-14 ${
                conv.id === selectedConversation ? "bg-blue-50" : "hover:bg-gray-100"
              }`}
              onMouseEnter={() => setIsHovering(conv.id ?? null)}
              onMouseLeave={() => setIsHovering(null)}
              onClick={() => handleConversationClick(conv)}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div
                  className={`truncate text-sm ${conv.id === selectedConversation ? "font-semibold" : "font-medium"}`}
                >
                  {getChatTitle(conv.messages ?? [], uiText.newChat)}
                </div>
                <div className="text-xs text-gray-400 tabular-nums">{formatDate(conv.createdAt ?? "")}</div>
              </div>
              {(isHovering === conv.id || (conv.id && deletingIds.has(conv.id)) || isMobile) && (
                <button
                  onClick={(e) => conv.id && handleDeleteClick(e, conv.id)}
                  disabled={conv.id ? deletingIds.has(conv.id) : false}
                  className="flex size-8 flex-none items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-secondary hover:text-danger-strong disabled:cursor-not-allowed max-md:size-11"
                  title={t("chat.deleteChat")}
                >
                  {conv.id && deletingIds.has(conv.id) ? (
                    <Loader2
                      className="animate-spin"
                      size={14}
                    />
                  ) : (
                    <Trash2Icon size={14} />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* The footer of the column is where a list-wide action lives (the mock
          puts «Удалить все чаты» here). Starting a chat is the one worth having:
          it is the reason to open this column, and deleting every chat at once
          is not something to keep one tap away. */}
        <div className="shrink-0 border-t border-border p-2">
          <button
            onClick={() => {
              onNewChat()
              if (isMobile) setShowConversationsList(false)
            }}
            className="flex min-h-9 w-full cursor-pointer items-center justify-center gap-2 rounded-lg text-sm font-medium text-primary transition-colors hover:bg-blue-50 max-md:min-h-11"
          >
            <PlusIcon />
            {t("chat.newChat")}
          </button>
        </div>
      </div>
      <ConfirmationModal />
    </>
  )
}

export const ConversationsList = React.memo(ConversationsListComponent)
